import { Message, MessageType } from '@/types/globals'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'

interface ChatState {
  messages: Message[]
  loading: boolean
}

const initialState: ChatState = {
  loading: false,
  messages: [],
}

const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    addMessage: (state, action: PayloadAction<Message>) => {
      state.messages.push({
        ...action.payload,
        disabled: false,
        type: action.payload.type ?? 'message', // ensure type is always set
      })
    },
    setMessage: (state, action: PayloadAction<Message[]>) => {
      state.messages = action.payload.map((msg) => ({
        ...msg,
        disabled: msg.disabled ?? false,
      }))
    },
    disableAllMessages: (state) => {
      state.messages.forEach((msg) => {
        if (!msg.disabled) {
          msg.disabled = true
        }
      })
    },
    disableTypedMessages: (state, action: PayloadAction<{ type?: MessageType }>) => {
      const { type } = action.payload
      state.messages.forEach((msg) => {
        if (!type || msg.type === type) {
          msg.disabled = true
        }
      })
    },
    resetChat: (state) => {
      state.messages = []
    },
    addStream: (state, action) => {
  // 🚫 Prevent streaming for system or user messages
  if (action.payload.sender === 'system' || action.payload.sender === 'user') {
    state.messages.push({
      ...action.payload,
      type: 'message', // not stream
    });
    return;
  }

  // ✅ Stream only AI messages
  state.messages.push(action.payload);
},
    appendStream: (state, action) => {
  // 🚫 Skip streaming for system or user senders
  if (action.payload.sender === 'system' || action.payload.sender === 'user') {
    return;
  }

  // ✅ Append stream content only for AI messages
  state.messages = state.messages.map((item: Message) => {
    if (item.type === action.payload.type && item.sender === 'ai') {
      return { ...item, content: item.content + action.payload.content };
    }
    return item;
  });
},
    resetStream: (state) => {
      const index = state.messages.findIndex((m: Message) => m.type === 'stream')
      if (index > -1) {
        const msg = state.messages[index]
        state.messages = [
          ...state.messages.filter((m: Message) => m.type !== 'stream'),
          { ...msg, type: 'idea-text' },
        ]
      }
    },
    resetStream_iterate: (state) => {
      const index = state.messages.findIndex((m: Message) => m.type === 'stream')
      if (index > -1) {
        const msg = state.messages[index]
        state.messages = [
          ...state.messages.filter((m: Message) => m.type !== 'stream'),
          { ...msg, type: 'message' }, // normalize to "message"
        ]
      }
    },
    setLoading: (state, action) => {
      state.loading = action.payload
    },
    updateMessage: (state, action: PayloadAction<Message>) => {
      state.messages = state.messages.map((m: Message) => {
        if (m?.id === action.payload.id) {
          return { ...action.payload }
        }
        return m
      })
    },
    // 🔹 NEW: Remove a message by id
    removeMessage: (state, action: PayloadAction<string | number>) => {
      state.messages = state.messages.filter((m: Message) => m.id !== action.payload)
    },
    markMessageDone: (state, action: PayloadAction<string | number>) => {
  state.messages = state.messages.map((m: Message) =>
    m.id === action.payload ? { ...m, done: true } : m
  )
},

  },
})

export const {
  addMessage,
  setMessage,
  disableAllMessages,
  disableTypedMessages,
  resetChat,
  resetStream_iterate,
  addStream,
  appendStream,
  resetStream,
  setLoading,
  updateMessage,
  removeMessage, // 👈 export it
  markMessageDone
} = chatSlice.actions

export default chatSlice.reducer
